/**
 * Valuation Dashboard Constants
 * Financial ratio benchmarks and method descriptions
 */

// Valuation method descriptions
export const METHOD_DESCRIPTIONS = {
  'cca': 'Comparable Company Analysis - Values your business by comparing it to similar publicly traded companies in your industry.',
  'dcf': 'Discounted Cash Flow - Calculates present value of projected future cash flows, ideal for businesses with predictable earnings.',
  'asset': 'Asset-Based Valuation - Values your business based on the fair market value of its net assets (assets minus liabilities).',
  'sde': 'Seller\'s Discretionary Earnings - Common for small businesses, adds back owner\'s salary and discretionary expenses to EBITDA.',
  'manual': 'Manual Multiple - Custom valuation using your specified multiple of revenue or EBITDA.',
  'multiple': 'Multiple of EBITDA - Industry standard approach using earnings before interest, taxes, depreciation, and amortization.'
};

// Ratio benchmarks - defines good, acceptable, and poor ranges
export const RATIO_BENCHMARKS = {
  // Profitability Ratios
  grossMargin: {
    good: 40, acceptable: 25, poor: 10,
    unit: '%', higher: true,
    description: 'Gross margin measures profitability after cost of goods sold',
    calculation: '(Gross Profit ÷ Revenue) × 100',
    interpretation: 'Higher gross margins indicate stronger pricing power and operational efficiency. Technology and service businesses typically have higher margins (50-80%) while retail and manufacturing may be lower (20-40%).'
  },
  ebitdaMargin: {
    good: 20, acceptable: 10, poor: 5,
    unit: '%', higher: true,
    description: 'EBITDA margin shows operational profitability before interest, taxes, depreciation, and amortization',
    calculation: '(EBITDA ÷ Revenue) × 100',
    interpretation: 'Strong EBITDA margins (>20%) indicate efficient operations. Industry standards vary: SaaS (20-30%), Manufacturing (10-15%), Retail (5-10%).'
  },

  // Liquidity Ratios
  currentRatio: {
    good: 2.0, acceptable: 1.0, poor: 0.5,
    unit: 'x', higher: true,
    description: 'Current ratio measures ability to pay short-term obligations',
    calculation: 'Current Assets ÷ Current Liabilities',
    interpretation: 'A ratio above 2.0 indicates strong short-term financial health. Between 1.0-2.0 is acceptable. Below 1.0 may indicate liquidity concerns.'
  },
  cashRatio: {
    good: 0.5, acceptable: 0.2, poor: 0.1,
    unit: 'x', higher: true,
    description: 'Cash ratio is the most conservative liquidity measure',
    calculation: 'Cash ÷ Current Liabilities',
    interpretation: 'Shows ability to pay current liabilities using only cash. >0.5 is excellent, 0.2-0.5 is acceptable, <0.2 may indicate cash flow concerns.'
  },

  // Leverage Ratios
  debtToEquity: {
    good: 0.5, acceptable: 1.5, poor: 3.0,
    unit: 'x', higher: false,
    description: 'Debt-to-equity ratio shows financial leverage and risk',
    calculation: 'Total Liabilities ÷ Equity',
    interpretation: 'Lower is generally better. <0.5 is conservative, 0.5-1.5 is moderate, >2.0 indicates high leverage and financial risk. Capital-intensive industries may have higher acceptable ratios.'
  },
  debtToAssets: {
    good: 30, acceptable: 50, poor: 70,
    unit: '%', higher: false,
    description: 'Debt-to-assets shows percentage of assets financed by debt',
    calculation: '(Total Liabilities ÷ Total Assets) × 100',
    interpretation: 'Lower percentages indicate less financial risk. <30% is strong, 30-50% is acceptable, >60% may indicate over-leverage.'
  },

  // Efficiency Ratios
  assetTurnover: {
    good: 2.0, acceptable: 1.0, poor: 0.5,
    unit: 'x', higher: true,
    description: 'Asset turnover measures how efficiently assets generate revenue',
    calculation: 'Revenue ÷ Total Assets',
    interpretation: 'Higher ratios indicate better asset efficiency. Retail (2-3x), Services (1-2x), Manufacturing (0.5-1.5x). Varies significantly by industry.'
  },
  roa: {
    good: 15, acceptable: 5, poor: 2,
    unit: '%', higher: true,
    description: 'Return on Assets (ROA) measures profitability relative to total assets',
    calculation: '(EBITDA ÷ Total Assets) × 100',
    interpretation: 'Higher ROA indicates more efficient use of assets. >15% is excellent, 5-15% is good, <5% may indicate inefficient operations.'
  },
  roe: {
    good: 20, acceptable: 10, poor: 5,
    unit: '%', higher: true,
    description: 'Return on Equity (ROE) measures profitability for shareholders',
    calculation: '(EBITDA ÷ Equity) × 100',
    interpretation: 'Higher ROE indicates better returns for shareholders. >20% is excellent, 10-20% is good, <10% may be concerning. Compare to industry peers.'
  },
};

// Category help content
export const CATEGORY_HELP_CONTENT = {
  profitability: {
    title: 'Profitability Ratios',
    description: 'Profitability ratios measure a company\'s ability to generate profit relative to revenue, assets, or equity. These ratios are critical indicators of business health and operational efficiency.',
    keyMetrics: ['Gross Margin: Profitability after COGS', 'EBITDA Margin: Operating profitability before financing costs'],
    interpretation: 'Strong profitability ratios indicate pricing power, cost control, and operational excellence. Compare to industry benchmarks and track trends over time.'
  },
  liquidity: {
    title: 'Liquidity Ratios',
    description: 'Liquidity ratios measure a company\'s ability to pay short-term obligations and meet immediate financial needs. These ratios are crucial for assessing financial stability.',
    keyMetrics: ['Current Ratio: Short-term solvency', 'Cash Ratio: Most conservative liquidity measure'],
    interpretation: 'Strong liquidity ratios provide financial flexibility and reduce risk during economic downturns. However, excess liquidity may indicate underutilized assets.'
  },
  leverage: {
    title: 'Leverage Ratios',
    description: 'Leverage ratios measure the degree to which a company uses debt financing. These ratios indicate financial risk and capital structure efficiency.',
    keyMetrics: ['Debt-to-Equity: Financial leverage', 'Debt-to-Assets: Asset financing structure'],
    interpretation: 'Moderate leverage can enhance returns, but excessive debt increases financial risk. Industry standards vary - capital-intensive businesses typically have higher leverage.'
  },
  efficiency: {
    title: 'Efficiency Ratios',
    description: 'Efficiency ratios measure how effectively a company uses its assets and resources to generate revenue and profit. These ratios indicate operational excellence.',
    keyMetrics: ['Asset Turnover: Revenue generation efficiency', 'ROA: Asset profitability', 'ROE: Shareholder returns'],
    interpretation: 'Higher efficiency ratios indicate better management of resources. These ratios vary significantly by industry and business model.'
  }
};

// Color mapping for ratio categories
export const CATEGORY_BACKGROUND_CLASSES = {
  profitability: {
    green: 'from-green-50 to-emerald-50 border-green-200',
    yellow: 'from-yellow-50 to-amber-50 border-yellow-200',
    red: 'from-red-50 to-rose-50 border-red-200'
  },
  liquidity: {
    green: 'from-blue-50 to-cyan-50 border-blue-200',
    yellow: 'from-yellow-50 to-amber-50 border-yellow-200',
    red: 'from-red-50 to-rose-50 border-red-200'
  },
  leverage: {
    green: 'from-purple-50 to-violet-50 border-purple-200',
    yellow: 'from-yellow-50 to-amber-50 border-yellow-200',
    red: 'from-red-50 to-rose-50 border-red-200'
  },
  efficiency: {
    green: 'from-teal-50 to-emerald-50 border-teal-200',
    yellow: 'from-yellow-50 to-amber-50 border-yellow-200',
    red: 'from-red-50 to-rose-50 border-red-200'
  }
};

// Text color mapping
export const STATUS_TEXT_COLORS = {
  green: 'text-green-700',
  yellow: 'text-amber-700',
  red: 'text-red-700'
};
